import { DateTime } from "luxon"
import React, { useCallback, useEffect, useRef, useState } from "react"

import { getHoursWrapped } from "@/components/calendar_view/_utils"
import SelectionPopover from "@/components/calendar_view/selection_popover"
import { useStore } from "@/store"

interface SelectionState {
  tableId: string
  startTime: DateTime
  endTime: DateTime
}

interface PendingClick {
  tableId: string
  time: DateTime
}

interface SelectionLayerProps {
  tables: { id: string }[]
  startHour: number
  endHour: number
  onSelectionActiveChange?: (active: boolean) => void
}

const SELECTION_DURATION_MINUTES = 15

const SelectionLayer: React.FC<SelectionLayerProps> = ({ tables, startHour, endHour, onSelectionActiveChange }) => {
  const { calendarDimensions, setShowQuickCreateReservationDialog, currentDate } = useStore()

  const [selection, setSelection] = useState<SelectionState | null>(null)
  const [showPopover, setShowPopover] = useState(false)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<{ tableId: string; time: DateTime } | null>(null)
  const [currentDrag, setCurrentDrag] = useState<{ tableId: string; time: DateTime } | null>(null)
  const [pendingClick, setPendingClick] = useState<PendingClick | null>(null)

  const dragStartRef = useRef<{ tableId: string; time: DateTime } | null>(null)
  const currentDragRef = useRef<{ tableId: string; time: DateTime } | null>(null)
  const hasDraggedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const selectionBoxRef = useRef<HTMLDivElement>(null)

  const hours = getHoursWrapped(startHour, endHour)

  const isSelectionActive = isDragging || selection !== null || pendingClick !== null
  useEffect(() => {
    onSelectionActiveChange?.(isSelectionActive)
  }, [isSelectionActive, onSelectionActiveChange])

  const getTimeFromY = useCallback(
    (y: number): DateTime | null => {
      if (!containerRef.current || !currentDate) return null
      const offset = calendarDimensions.timeline.offset
      if (!offset) return null

      const rect = containerRef.current.getBoundingClientRect()
      const relativeY = y - rect.top
      const adjustedY = relativeY - offset / 2

      const totalMinutes = hours.length * 60
      const timelineHeight = hours.length * offset
      const percentage = adjustedY / timelineHeight
      const minutes = percentage * totalMinutes
      const roundedMinutes = Math.round(minutes / 15) * 15
      const clampedMinutes = Math.max(0, Math.min(totalMinutes - 15, roundedMinutes))

      const hourIndex = Math.floor(clampedMinutes / 60)
      const minuteOfHour = clampedMinutes % 60
      const hour = hours[hourIndex] ?? hours[hours.length - 1]

      return currentDate.set({ hour, minute: minuteOfHour, second: 0, millisecond: 0 })
    },
    [calendarDimensions.timeline.offset, hours, currentDate],
  )

  const getTableFromX = useCallback(
    (x: number): string | null => {
      if (!containerRef.current) return null
      const columnWidth = calendarDimensions.columnWidth
      if (!columnWidth) return null

      const rect = containerRef.current.getBoundingClientRect()
      const relativeX = x - rect.left
      const tableIndex = Math.floor(relativeX / columnWidth)
      return tables[tableIndex]?.id ?? null
    },
    [calendarDimensions.columnWidth, tables],
  )

  const openPopover = useCallback(() => {
    if (selectionBoxRef.current) {
      setPopoverAnchor(selectionBoxRef.current)
      setShowPopover(true)
    }
  }, [])

  const closePopover = useCallback(() => {
    setShowPopover(false)
    setPopoverAnchor(null)
    setSelection(null)
    setPendingClick(null)
  }, [])

  const handleReserve = useCallback(() => {
    if (selection) {
      const startTime = selection.startTime.toISO()
      const endTime = selection.endTime.toISO()
      if (!startTime || !endTime) {
        closePopover()
        return
      }
      setShowQuickCreateReservationDialog(true, {
        tableId: selection.tableId,
        startTime,
        endTime,
      })
    }
    closePopover()
  }, [selection, setShowQuickCreateReservationDialog, closePopover])

  const handleWalkIn = useCallback(() => {
    if (selection) {
      const startTime = selection.startTime.toISO()
      const endTime = selection.endTime.toISO()
      if (!startTime || !endTime) {
        closePopover()
        return
      }
      setShowQuickCreateReservationDialog(true, {
        tableId: selection.tableId,
        startTime,
        endTime,
        reservationType: "walk_in",
      })
    }
    closePopover()
  }, [selection, setShowQuickCreateReservationDialog, closePopover])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (showPopover) {
        return
      }

      const tableId = getTableFromX(e.clientX)
      const time = getTimeFromY(e.clientY)

      if (!tableId || !time) return

      hasDraggedRef.current = false
      setIsDragging(true)
      setDragStart({ tableId, time })
      setCurrentDrag({ tableId, time })
      dragStartRef.current = { tableId, time }
      currentDragRef.current = { tableId, time }
      setSelection(null)
      setShowPopover(false)
    },
    [getTableFromX, getTimeFromY, showPopover],
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (showPopover) {
        return
      }

      const touch = e.touches[0]
      const tableId = getTableFromX(touch.clientX)
      const time = getTimeFromY(touch.clientY)

      if (!tableId || !time) return

      hasDraggedRef.current = false
      setIsDragging(true)
      setDragStart({ tableId, time })
      setCurrentDrag({ tableId, time })
      dragStartRef.current = { tableId, time }
      currentDragRef.current = { tableId, time }
      setSelection(null)
      setShowPopover(false)
    },
    [getTableFromX, getTimeFromY, showPopover],
  )

  useEffect(() => {
    if (!isDragging) return

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY
      const time = getTimeFromY(clientY)
      if (time && dragStartRef.current) {
        if (!time.equals(dragStartRef.current.time)) {
          hasDraggedRef.current = true
        }
        setCurrentDrag({ tableId: dragStartRef.current.tableId, time })
        currentDragRef.current = { tableId: dragStartRef.current.tableId, time }
      }
    }

    const handleUp = () => {
      const start = dragStartRef.current
      const current = currentDragRef.current
      const hasDragged = hasDraggedRef.current

      if (start && current) {
        if (hasDragged) {
          // Use pendingClick as the anchor if it exists and is on the same table
          const anchor = pendingClick && pendingClick.tableId === start.tableId ? pendingClick.time : start.time

          const startTime = anchor.toMillis() < current.time.toMillis() ? anchor : current.time
          let endTime = anchor.toMillis() < current.time.toMillis() ? current.time : anchor

          if (startTime.equals(endTime)) {
            endTime = startTime.plus({ minutes: SELECTION_DURATION_MINUTES })
          }

          setSelection({
            tableId: start.tableId,
            startTime,
            endTime,
          })
          setPendingClick(null)
          setTimeout(openPopover, 0)
        } else {
          setPendingClick((prev) => {
            if (prev && prev.tableId === start.tableId) {
              const startTime = prev.time.toMillis() < start.time.toMillis() ? prev.time : start.time
              let endTime = prev.time.toMillis() < start.time.toMillis() ? start.time : prev.time

              if (startTime.equals(endTime)) {
                endTime = startTime.plus({ minutes: SELECTION_DURATION_MINUTES })
              }

              setSelection({
                tableId: start.tableId,
                startTime,
                endTime,
              })
              setTimeout(openPopover, 0)
              return null
            }
            setSelection({
              tableId: start.tableId,
              startTime: start.time,
              endTime: start.time.plus({ minutes: SELECTION_DURATION_MINUTES }),
            })
            return { tableId: start.tableId, time: start.time }
          })
        }
      }

      setIsDragging(false)
      setDragStart(null)
      setCurrentDrag(null)
      dragStartRef.current = null
      currentDragRef.current = null
      hasDraggedRef.current = false
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDragging(false)
        setDragStart(null)
        setCurrentDrag(null)
        dragStartRef.current = null
        currentDragRef.current = null
        hasDraggedRef.current = false
        setSelection(null)
        setPendingClick(null)
        setShowPopover(false)
      }
    }

    window.addEventListener("mousemove", handleMove)
    window.addEventListener("touchmove", handleMove)
    window.addEventListener("mouseup", handleUp)
    window.addEventListener("touchend", handleUp)
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("touchmove", handleMove)
      window.removeEventListener("mouseup", handleUp)
      window.removeEventListener("touchend", handleUp)
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isDragging, getTimeFromY, openPopover, pendingClick])

  useEffect(() => {
    if (!selection || showPopover) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelection(null)
        setPendingClick(null)
        setShowPopover(false)
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [selection, showPopover])

  const getSelectionPosition = useCallback(
    (tableId: string, startTime: DateTime, endTime: DateTime) => {
      const position = calendarDimensions.getReservationPosition({
        tableId,
        startTime,
        endTime,
      })
      return position
    },
    [calendarDimensions],
  )

  const renderSelectionBox = () => {
    let displaySelection: SelectionState | null = null
    let showEndTime = false

    if (isDragging && dragStart && currentDrag) {
      if (pendingClick && pendingClick.tableId === dragStart.tableId) {
        const startTime =
          pendingClick.time.toMillis() < currentDrag.time.toMillis() ? pendingClick.time : currentDrag.time
        let endTime = pendingClick.time.toMillis() < currentDrag.time.toMillis() ? currentDrag.time : pendingClick.time
        showEndTime = !startTime.equals(endTime)
        if (startTime.equals(endTime)) {
          endTime = startTime.plus({ minutes: SELECTION_DURATION_MINUTES })
        }
        displaySelection = {
          tableId: dragStart.tableId,
          startTime,
          endTime,
        }
      } else {
        const startTime = dragStart.time.toMillis() < currentDrag.time.toMillis() ? dragStart.time : currentDrag.time
        let endTime = dragStart.time.toMillis() < currentDrag.time.toMillis() ? currentDrag.time : dragStart.time
        showEndTime = !startTime.equals(endTime)
        if (startTime.equals(endTime)) {
          endTime = startTime.plus({ minutes: SELECTION_DURATION_MINUTES })
        }
        displaySelection = {
          tableId: dragStart.tableId,
          startTime,
          endTime,
        }
      }
    } else if (selection) {
      displaySelection = selection
      showEndTime =
        showPopover ||
        selection.endTime.toMillis() - selection.startTime.toMillis() > SELECTION_DURATION_MINUTES * 60 * 1000
    }

    if (!displaySelection) return null

    const position = getSelectionPosition(
      displaySelection.tableId,
      displaySelection.startTime,
      displaySelection.endTime,
    )
    if (!position) return null

    return (
      <div
        ref={selectionBoxRef}
        className="absolute bg-gray-300 rounded-md mx-1 flex items-center justify-center text-gray-600 text-sm pointer-events-none z-10"
        style={{
          top: position.top,
          left: position.left,
          width: position.width - 8,
          height: position.height,
          marginLeft: 4,
        }}
      >
        {showEndTime
          ? `${displaySelection.startTime.toFormat("HH:mm")} - ${displaySelection.endTime.toFormat("HH:mm")}`
          : displaySelection.startTime.toFormat("HH:mm")}
      </div>
    )
  }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- drag-to-select overlay; keyboard selection is not supported by design */}
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{
          width: calendarDimensions.canvas.width,
          height: calendarDimensions.canvas.height,
          cursor: "pointer",
          zIndex: 15,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid="panel-reservations-calendar-selection"
      >
        {renderSelectionBox()}
      </div>
      {selection ? (
        <SelectionPopover
          open={showPopover}
          anchorEl={popoverAnchor}
          startTime={selection.startTime.toFormat("HH:mm")}
          endTime={selection.endTime.toFormat("HH:mm")}
          onReserve={handleReserve}
          onWalkIn={handleWalkIn}
          onClose={closePopover}
        />
      ) : null}
    </>
  )
}

export default SelectionLayer
